import { useEffect, useState } from "react";
import { toast } from "sonner";

import { BrandModal } from "@/components/common/BrandModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useInterests,
  useInterestCategories,
} from "@/hooks/queries/useInterests";
import { useUpdateInterests } from "@/hooks/mutations/useInterestMutations";
import { TermChip } from "./TermChip";
import type { UserInterest } from "@/types/domain";

export function InterestsDialog({
  searchProfileId,
  open,
  onOpenChange,
}: {
  searchProfileId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: existing = [] } = useInterests(searchProfileId);
  const { data: categories = [] } = useInterestCategories();
  const updateMutation = useUpdateInterests();

  const [items, setItems] = useState<UserInterest[]>([]);
  const [keyword, setKeyword] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");

  // Solo se siembra al abrir: con `existing` en las dependencias, un refetch en
  // segundo plano pisaba las ediciones sin guardar.
  useEffect(() => {
    if (open) setItems(existing);
  }, [open, searchProfileId]);

  function addItem() {
    // Se guarda tal cual: el espacio al inicio/final es intencional
    // (ej. "NiFF " no debe matchear "NIFFX").
    if (!keyword.trim()) return;
    setItems((prev) => [
      ...prev,
      {
        keyword,
        priceitemcategory_id: categoryId ? Number(categoryId) : null,
        category_name:
          categories.find((c) => c.priceitemcategory_id === Number(categoryId))
            ?.priceitemcategory_name ?? null,
      },
    ]);
    setKeyword("");
    setCategoryId("");
  }

  function renameItem(index: number, next: string) {
    setItems((p) =>
      p.map((it, i) => (i === index ? { ...it, keyword: next } : it)),
    );
  }

  function toggleItem(index: number) {
    setItems((p) =>
      p.map((it, i) =>
        i === index ? { ...it, active: !(it.active ?? true) } : it,
      ),
    );
  }

  function save() {
    updateMutation.mutate(
      {
        // `active` viaja siempre: el backend borra y recrea, así que omitirlo
        // reactivaría todo lo que el usuario había apagado.
        interests: items.map((i) => ({
          keyword: i.keyword,
          active: i.active ?? true,
          priceitemcategory_id: i.priceitemcategory_id,
        })),
        searchProfileId,
      },
      {
        onSuccess: () => {
          toast.success("Intereses actualizados");
          onOpenChange(false);
        },
        onError: () => toast.error("No se pudieron guardar los intereses"),
      },
    );
  }

  return (
    <BrandModal
      open={open}
      onOpenChange={onOpenChange}
      title="Gestionar tus intereses"
      description={
        <>
          Define los términos clave que deseas rastrear (ej. "geomalla",
          "cemento"). El sistema buscará estos términos dentro de los análisis
          de costos unitarios e insumos de las licitaciones.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Guardando..." : "Guardar cambios"}
          </Button>
        </>
      }
    >
      <div className="flex items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <Label className="text-sm font-semibold">Recurso</Label>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
          />
        </div>
        <div className="w-40 space-y-1.5">
          <Label className="text-sm font-semibold">Rubro (Opcional)</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder=" " />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem
                  key={c.priceitemcategory_id}
                  value={String(c.priceitemcategory_id)}
                >
                  {c.priceitemcategory_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="secondary"
          onClick={addItem}
          disabled={!keyword.trim()}
        >
          + Agregar
        </Button>
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-bold">Tus palabras clave de interés:</p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No has agregado ningún término clave todavía. Usa el formulario de
            arriba para agregar tus intereses.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {items.map((item, i) => (
              <TermChip
                key={i}
                term={item.keyword}
                tag={item.category_name}
                active={item.active ?? true}
                onRemove={() =>
                  setItems((p) => p.filter((_, idx) => idx !== i))
                }
                onRename={(next) => renameItem(i, next)}
                onToggle={() => toggleItem(i)}
              />
            ))}
          </div>
        )}
      </div>
    </BrandModal>
  );
}
